import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import CsvStepIndicator from "@/components/csv/CsvStepIndicator";
import CsvUploadStep from "@/components/csv/CsvUploadStep";
import CsvMappingStep from "@/components/csv/CsvMappingStep";
import CsvResultStep from "@/components/csv/CsvResultStep";
import { suggestMapping } from "@/components/csv/csvAliases";

// Orchestrator for the 3-step CSV wizard.
// Each step lives in its own component under /components/csv; this page
// only owns the transitions between them and the network calls that bridge
// upload → preview → apply → result.
export default function CsvUpload() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [affiliateId, setAffiliateId] = useState("");
  const [forceTest, setForceTest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data.campaigns));
    api.get("/organizations?type=affiliate").then((r) => setAffiliates(r.data.organizations));
  }, []);

  const doPreview = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/csv-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(suggestMapping(data.headers, data.canonical_fields));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/csv-import/apply", {
        import_id: preview.import_id,
        mapping,
        static_values: {},
        campaign_id: campaignId || null,
        affiliate_id: affiliateId || null,
        is_test: forceTest,
      });
      setResult(data);
      toast.success(`Imported ${data.created}`);
      // Notify the global "Undo import" banner so it refreshes immediately
      // instead of waiting for its 15 s server poll.
      window.dispatchEvent(new CustomEvent("csv-import-complete"));
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setPreview(null);
    setMapping({}); setResult(null);
  };

  return (
    <div className="max-w-4xl">
      <div className="label-mono">Bulk intake</div>
      <h1 className="font-chivo font-black text-3xl uppercase tracking-tighter">CSV upload wizard</h1>

      <CsvStepIndicator current={step} />

      {step === 1 && (
        <CsvUploadStep file={file} onFile={setFile} onSubmit={doPreview} busy={busy} />
      )}

      {step === 2 && preview && (
        <CsvMappingStep
          preview={preview}
          mapping={mapping} setMapping={setMapping}
          campaigns={campaigns} affiliates={affiliates}
          campaignId={campaignId} setCampaignId={setCampaignId}
          affiliateId={affiliateId} setAffiliateId={setAffiliateId}
          forceTest={forceTest} setForceTest={setForceTest}
          onBack={() => { setStep(1); setPreview(null); }}
          onApply={apply}
          busy={busy}
        />
      )}

      {step === 3 && result && (
        <CsvResultStep
          result={result}
          onReset={reset}
          onGoToLeads={() => nav("/leads")}
        />
      )}
    </div>
  );
}
